import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideSteps,
  GuideStep,
  GuideGrid,
  GuideCard,
  GuideDarkSection
} from '@/components/guides/guide-layout';
import { Link } from 'react-router';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function WhatToExpectAtHearingGuide() {
  return (
    <GuideLayout
      slug="what-to-expect-at-hearing"
      category="Escalation"
      categoryColor="warning"
      title="What to Expect at Your Hearing"
      description="How small claims hearings work and how to prepare for presenting your pothole case to a judge."
      readTime="8 min"
      updatedDate="2026-01-12"
      datePublished="2026-01-10"
      reviewedFor="UK law"
      sources={[
        { name: 'Gov.uk - Making a court claim', url: 'https://www.gov.uk/make-court-claim-for-money' },
      ]}
      relatedGuides={[
        { slug: 'small-claims-court-guide', title: 'Small Claims Court Guide', readTime: '10 min' },
        { slug: 'understanding-rejection-reasons', title: 'Common Rejection Reasons', readTime: '6 min' },
        { slug: 'section-58-defence', title: 'The Section 58 Defence', readTime: '7 min' },
      ]}
      atAGlance={[
        { label: 'Hearing length', value: 'Typically 30-60 minutes for a pothole case' },
        { label: 'Setting', value: 'Informal — a small hearing room, not a TV-style courtroom' },
        { label: 'Representation', value: 'Not needed — small claims is designed for self-representation' },
        { label: 'What to bring', value: 'Evidence bundle, one-page written summary, notes, and a timeline of events' },
        { label: 'Cost risk if you lose', value: "Usually just your court fee — not the council's legal costs" },
      ]}
      howTo={{
        name: 'How to Present Your Pothole Case at a Small Claims Hearing',
        description:
          'Four steps for presenting a pothole damage claim to a district judge at a small claims hearing.',
        steps: [
          {
            name: 'Opening: tell your story',
            text: 'Explain the claim simply and chronologically: where and when the incident happened, what was damaged, the evidence held, and the repair cost being claimed.',
          },
          {
            name: 'Evidence: show your proof',
            text: 'Walk the judge calmly through the photos of the pothole and the damage, the repair invoice or quote, and any prior reports showing the council knew about the defect.',
          },
          {
            name: 'Counter the council',
            text: 'When the council presents its Section 58 defence, respond factually: ask for inspection records, highlight any prior reports, and point out that a defect that caused damage was dangerous.',
          },
          {
            name: 'Closing: summarise',
            text: 'Briefly restate that the council had a duty to maintain the road, knew or should have known about the defect, and that its failure caused the damage being claimed.',
          },
        ],
      }}
      faqs={[
        {
          question: 'Do I need a lawyer for small claims court?',
          answer: 'No, small claims court is designed for people to represent themselves. Lawyers are allowed but unusual for claims under £10,000. The judge will help ensure you understand the process.',
        },
        {
          question: 'What should I wear to court?',
          answer: 'Smart casual is appropriate. You don\'t need a suit, but avoid jeans and trainers. Dress as you would for a job interview — respectfully but not formally.',
        },
        {
          question: 'How long does a hearing take?',
          answer: 'Most small claims hearings for pothole cases last 30-60 minutes. Complex cases might take longer, but judges aim to resolve matters efficiently.',
        },
        {
          question: 'Can I bring a friend or family member?',
          answer: 'Yes, you can bring someone for support. They can sit in the hearing but generally shouldn\'t speak unless the judge asks them to.',
        },
        {
          question: 'What if I get nervous?',
          answer: 'That\'s completely normal. The judge knows you\'re not a lawyer and will make allowances. Take your time, speak clearly, and refer to your prepared notes if needed.',
        },
        {
          question: 'What if I lose?',
          answer: 'In small claims, even if you lose, you typically don\'t have to pay the other side\'s legal costs (unless you behaved unreasonably). You\'d just lose your court fee and not receive compensation.',
        },
        {
          question: 'Can the council send a lawyer against me?',
          answer: 'Yes, but often they send an insurance representative or even just written submissions. Even if they have a lawyer, the judge ensures fair treatment of self-represented claimants.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
        title="Court Isn't Scary"
      >
        <p className="mb-4">
          <Link to="/guides/small-claims-court-guide" className={inlineLink}>
            Small claims court
          </Link>
          {' '}exists specifically for ordinary people to resolve disputes without lawyers. 
          It's informal, the judge guides you through the process, and you just need to tell your story 
          and present your evidence. No wigs, no jargon, no tricks.
        </p>
        <GuideTip variant="success" title="Good news">
          Many councils settle once you file the court claim, often before the hearing date. They'd 
          rather pay your claim than send someone to court. So you may never need to appear.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Hearing Day Timeline"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <span className="font-mono text-brand-300 font-bold">09:00</span>
            <div>
              <p className="text-white font-semibold mb-1">Arrive Early</p>
              <p className="text-brand-300 text-sm">Get there 30 minutes before your slot. Find the waiting area and check in with the usher.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <span className="font-mono text-brand-300 font-bold">09:30</span>
            <div>
              <p className="text-white font-semibold mb-1">Wait to Be Called</p>
              <p className="text-brand-300 text-sm">You'll wait in a public area. Use this time to review your notes. The council's representative may also be waiting.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-accent-500">
            <span className="font-mono text-accent-400 font-bold">10:00</span>
            <div>
              <p className="text-white font-semibold mb-1">Enter the Hearing Room</p>
              <p className="text-brand-300 text-sm">A small room with a judge's desk and seating for parties. Much less formal than TV courts.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-accent-500">
            <span className="font-mono text-accent-400 font-bold">10:05</span>
            <div>
              <p className="text-white font-semibold mb-1">Present Your Case</p>
              <p className="text-brand-300 text-sm">The judge will ask you to explain what happened. Keep it simple: pothole, damage, evidence, council's failure.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-accent-500">
            <span className="font-mono text-accent-400 font-bold">10:20</span>
            <div>
              <p className="text-white font-semibold mb-1">Council Responds</p>
              <p className="text-brand-300 text-sm">They'll explain their position — usually the Section 58 defence. The judge may ask them questions.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-success-500">
            <span className="font-mono text-success-300 font-bold">10:40</span>
            <div>
              <p className="text-white font-semibold mb-1">Questions & Decision</p>
              <p className="text-brand-300 text-sm">The judge may ask questions, then usually gives a decision on the spot or reserves it for later.</p>
            </div>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        }
        title="What to Bring"
      >
        <GuideGrid cols={2}>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            title="Your Evidence Bundle"
          >
            Photos,{' '}
            <Link to="/guides/getting-repair-quotes" className={inlineLink}>
              repair quotes/invoices
            </Link>
            , location maps, prior reports — everything you submitted with your claim.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            }
            title="Written Summary"
          >
            A one-page summary of your case: what happened, what damage, what you're claiming, why the council is liable.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Notes & Questions"
          >
            Key points you want to make, and questions to ask the council if they claim something.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Timeline of Events"
          >
            Dates of incident, report submissions, council communications, and rejection.
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideSteps>
        <GuideStep number={1} title="Opening: Tell Your Story">
          <p className="mb-2">
            The judge will ask you to explain your claim. Keep it simple and chronological:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• "On [date], I was driving on [road name]..."</li>
            <li>• "I hit a pothole and my [tyre/wheel/etc.] was damaged..."</li>
            <li>• "I have photos of the pothole and the damage..."</li>
            <li>• "The repair cost was £[amount]..."</li>
          </ul>
        </GuideStep>

        <GuideStep number={2} title="Evidence: Show Your Proof">
          <p className="mb-2">
            Walk through your evidence calmly. Let the judge see:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>
              •{' '}
              <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
                Photos of the pothole
              </Link>
              {' '}(pointing out size/depth)
            </li>
            <li>• Photos of your damage</li>
            <li>• The repair invoice or quote</li>
            <li>
              • Any{' '}
              <Link to="/guides/prior-reports-explained" className={inlineLink}>
                prior reports
              </Link>
              {' '}showing the council knew
            </li>
          </ul>
        </GuideStep>

        <GuideStep number={3} title="Counter the Council">
          <p className="mb-2">
            When the council presents their{' '}
            <Link to="/guides/section-58-defence" className={inlineLink}>
              Section 58 defence
            </Link>
            , be ready to respond:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• If they claim adequate inspections: ask for records</li>
            <li>• If you have prior reports: highlight them</li>
            <li>• If they say the pothole was too small: it caused damage</li>
            <li>• Stay calm and factual, not emotional</li>
          </ul>
        </GuideStep>

        <GuideStep number={4} title="Closing: Summarise">
          <p className="mb-2">
            The judge may ask for a final statement. Keep it brief:
          </p>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• "The council had a duty to maintain this road"</li>
            <li>• "They knew about the defect [or should have found it]"</li>
            <li>• "Their failure caused my damage"</li>
            <li>• "I'm claiming £[amount] for my repair costs"</li>
          </ul>
        </GuideStep>
      </GuideSteps>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Tips for Success"
      >
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Be polite and respectful.</strong> Address the judge as "Sir" or "Ma'am" or "Your Honour".</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Don't interrupt.</strong> Let others finish speaking before you respond.</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Stick to the facts.</strong> Emotion doesn't win cases — evidence does.</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Know your numbers.</strong> Be clear on dates, amounts, and measurements.</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-surface-700"><strong>Ask if unsure.</strong> It's okay to ask the judge to clarify something.</span>
          </li>
        </ul>
      </GuideSection>
    </GuideLayout>
  );
}
